import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { Spin } from "antd5";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import { UserInitialsType } from "components/comments/UserInitials";
import CustomerDocumentSelector from "components/form_components/CustomerDocumentSelector";
import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import Popover from "components/ui/popover/Popover";
import { copyToClipboard } from "lib/clipboard";
import { AiQuestionAndAnswer } from "lib/core_components/ai/AiQuestionAndAnswer";
import AiSources from "lib/core_components/ai/AiSources";
import { AiResourceType } from "lib/core_components/ai/types";
import Badge from "lib/core_components/Badge";
import { useAiChatManager } from "lib/hooks/api/ai_chat/useAiChatManager";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useAiContext } from "lib/providers/AiContextProvider";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { AiChatStreamResourceType, CustomerDocumentTypeFilter } from "lib/types/graphQLEnums";
import { toast } from "../ui/toast/Toast";
import AiChatHistory from "./AiChatHistory";
import AiChatSuggestedQuestionContainer from "./AiChatSuggestedQuestionContainer";
import { AiChatInputExtension } from "./inputExtension";
import {
  AiChatResourceType,
  AiConversation,
  AiDocumentSource,
  AIQuestionForm,
  AiSource,
  SuggestedQuestion,
} from "./types";

type UiText = {
  title?: string;
  subtitle?: string;
  inputPlaceholder?: string;
};

const defaultText: UiText = {
  title: "Ask Stotles AI",
  subtitle: "Ask questions about anything from Stotles AI.",
  inputPlaceholder: "Ask anything",
};

function getResourceTypeEnum(
  resourceType: AiResourceType | AiChatResourceType,
): AiChatStreamResourceType {
  switch (resourceType) {
    case "BUYER_QA":
      return AiChatStreamResourceType.BuyerQa;
    case "BID_ASK_AI":
      return AiChatStreamResourceType.BidAskAi;
    case "BID_WRITING_QA":
      return AiChatStreamResourceType.BidWritingQa;
    case "CONTENT_LIBRARY_QA":
      return AiChatStreamResourceType.ContentLibraryQa;
    case "DOCUMENT_SINGLE_QA":
      return AiChatStreamResourceType.DocumentSingleQa;
    default:
      throw new Error(`Unsupported resource type for AI chat: ${resourceType}`);
  }
}

type SourceSelector = {
  title?: string;
  documentType: CustomerDocumentTypeFilter;
  inputPlaceholder?: string;
};

/**
 * Chooses a suggested question or quick prompt, as clicking one of the suggested questions does.
 * @param trackingProperties - Extra properties for its "Quick prompt clicked" event
 */
type SelectSuggestedQuestion = (prompt: string, trackingProperties?: EventData) => Promise<void>;

// What renderInput and renderEmptyThread build their UI with
type AiChatRenderProps = {
  methods: UseFormReturn<AIQuestionForm>;
  handleQuestionSubmit: (
    isQuickPrompt?: boolean,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
  selectSuggestedQuestion: SelectSuggestedQuestion;
};

type AIChatProps = {
  additionalContext?: string;
  renderSaveQuestion?: () => JSX.Element;
  renderInput?: (
    props: AiChatRenderProps & { currentConversation?: AiConversation },
  ) => JSX.Element;
  renderEmptyThread?: (props: AiChatRenderProps) => JSX.Element;
  renderActions?: (answer: string, conversationId: string, sources?: AiSource[]) => JSX.Element;
  suggestedQuestions?: SuggestedQuestion[];
  defaultSelectedSources?: string[];
  showHistory?: boolean;
  historyPlacement?: "sidebar" | "inline";
  inlineHistoryContainerPadding?: string;
  showDisclaimer?: boolean;
  uiText?: UiText;
  suggestedQuestionsLayout?: "vertical" | "horizontal";
  userInitialsType?: UserInitialsType;
  mainContainerPadding?: string;
  chatThreadContainerPadding?: string;
  showCopyToClipboardButton?: boolean;
  sourceSelector?: SourceSelector;
  inputExtension?: AiChatInputExtension;
  autoAnswerSuggestedPrompt?: boolean;
  toneOfVoiceId?: string;
  toneOfVoiceLabel?: string;
  onNewConversation?: () => void;
  /**
   * Optional callback fired when a user clicks a document citation in the
   * answer. When provided, overrides the default behaviour of opening a
   * document drawer — useful when the cited document is already rendered
   * in-page (e.g. the document details PDF viewer).
   */
  onDocumentSourceClick?: (source: AiDocumentSource) => void;
};

/**
 * AiChatPanel component - UI for interacting with AI chat, handling questions, streaming responses, and history
 *
 * This panel can be used to build AI chat interfaces for various resources, such as buyers or bid asks. This can be
 * wrapped in AiChatModal to create a modal dialog for AI chat
 *
 * @param props.additionalContext - Additional context to provide to the AI chat
 * @param props.renderSaveQuestion - Optional render function for a save question button
 * @param props.renderInput - Optional render function for displaying custom input field. Has access to react-hook-form methods, handleQuestionSubmit and selectSuggestedQuestion
 * @param props.renderEmptyThread - Optional render function for displaying custom placeholder for empty thread. Also gets selectSuggestedQuestion, e.g. for a quick prompts menu
 * @param props.suggestedQuestions - List of suggested questions to display when no conversation
 * @param props.showHistory - Whether to show conversation history panel
 * @param props.showDisclaimer - Whether to show disclaimer message at the bottom of the chat
 * @param props.uiText - Override text for title, subtitle, and input placeholder
 * @param props.suggestedQuestionsLayout - Layout of suggested questions, either "vertical" or "horizontal". The default is "horizontal"
 * @param props.userInitialsType - Type of user initials to display in the chat (default, small, or extra-small)
 * @param props.mainContainerPadding - Padding for the main container. Defaults to "16px 48px 16px 32px"
 * @param props.chatThreadContainerPadding - Padding for the chat thread container. Defaults to "48px 16px 0 0". If changing, better to leave space in the right for the scrollbar
 * @param props.showCopyToClipboardButton - Whether to show the copy to clipboard button
 * @param props.sourceSelector - Source selector options. If provided, a source selector dropdown will be rendered
 */
export default function AiChatPanel({
  additionalContext,
  renderSaveQuestion,
  renderInput,
  renderEmptyThread,
  renderActions,
  suggestedQuestions = [],
  defaultSelectedSources = [],
  showHistory = true,
  historyPlacement = "sidebar",
  inlineHistoryContainerPadding,
  showDisclaimer = true,
  uiText,
  suggestedQuestionsLayout,
  userInitialsType,
  mainContainerPadding,
  chatThreadContainerPadding,
  showCopyToClipboardButton = true,
  sourceSelector,
  inputExtension,
  autoAnswerSuggestedPrompt = true,
  toneOfVoiceId,
  toneOfVoiceLabel,
  onNewConversation,
  onDocumentSourceClick,
}: AIChatProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  const hasSourceSelector = !!sourceSelector;

  const { resourceId, resourceName, resourceType } = useAiContext();

  const [inlineHistoryVisible, setInlineHistoryVisible] = useState(false);

  const questionFormSchema = useMemo(
    () =>
      z
        .object({
          userInputQuestion: z.string().min(10, {
            message: "Add more characters",
          }),
          selectedSources: z.array(z.string().uuid()).optional(),
          sources: z
            .object({
              notices: z.boolean().optional(),
              documents: z.boolean().optional(),
            })
            .optional(),
        })
        .superRefine((data, ctx) => {
          inputExtension?.refineQuestionForm?.(data, ctx, { resourceType });
        }),
    [inputExtension, resourceType],
  );

  const methods = useForm<AIQuestionForm>({
    mode: "onSubmit",
    defaultValues: {
      userInputQuestion: "",
      selectedSources: defaultSelectedSources,
      ...(inputExtension?.getDefaultValues?.({ resourceType }) ?? {}),
    },
    resolver: zodResolver(questionFormSchema),
  });

  // Reset selected sources only when the defaultSelectedSources prop itself changes value (not when
  // the user changes their selection), e.g. in ContentLibraryAiChatModal where defaultSelectedSources
  // gets populated after the customer documents query resolves
  const prevDefaultSelectedSourcesRef = useRef(defaultSelectedSources);
  useEffect(() => {
    const prev = prevDefaultSelectedSourcesRef.current;
    const defaultSourcesChanged =
      JSON.stringify([...prev].sort()) !== JSON.stringify([...defaultSelectedSources].sort());

    prevDefaultSelectedSourcesRef.current = defaultSelectedSources;

    if (defaultSourcesChanged) {
      const currentValues = methods.getValues();
      methods.reset({
        ...currentValues,
        selectedSources: defaultSelectedSources,
      });
    }
  }, [defaultSelectedSources, methods]);

  useEffect(() => {
    const currentValues = methods.getValues();
    methods.reset({
      ...currentValues,
      sources: undefined,
      ...(inputExtension?.getDefaultValues?.({ resourceType }) ?? {}),
    });
  }, [inputExtension, resourceType, methods]);

  const { control, handleSubmit, setValue, formState, watch, getValues } = methods;

  const selectedSources = watch("selectedSources");
  const values = watch();
  const hasExtensionSubmitError =
    inputExtension?.isSubmitDisabled?.({ values: getValues(), resourceType }) ?? false;

  const scrollToBottomOfAiChat = () => {
    setTimeout(() => {
      containerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  const {
    chatId,
    setChatId,
    conversation,
    thinking,
    isLoading,
    isWaitingForAnswerToStart,
    startAiChatSubscription,
    resetAiChatSubscription,
  } = useAiChatManager();

  const questionEventProperties = (question: string): EventData => ({
    "Question asked": question,
    "Resource Id": resourceId,
    "Resource name": resourceName,
    "Context source": "Ask AI",
    "Conversation Id": chatId,
    ...(toneOfVoiceLabel !== undefined && {
      "Tone of voice label": toneOfVoiceLabel,
      "Non-default tone of voice": toneOfVoiceLabel !== "Default",
    }),
  });

  const handleQuestionSubmit = (isQuickPrompt?: boolean, trackingProperties?: EventData) =>
    handleSubmit(({ userInputQuestion, selectedSources, ...restValues }) => {
      // clear the previous subscription before streaming a new response
      // otherwise, if the socket ever disconnects (like when the browser tab has been idle for a while)
      // the subscription will not be restarted and the user will see like the question is not working
      resetAiChatSubscription();
      const eventName = isQuickPrompt ? EventNames.quickPromptClicked : EventNames.newQuestionAsked;
      logEvent(eventName, { ...questionEventProperties(userInputQuestion), ...trackingProperties });

      startAiChatSubscription({
        req: {
          question: userInputQuestion,
          resourceId,
          resourceType: getResourceTypeEnum(resourceType),
          documentIds: selectedSources,
          conversationId: chatId || undefined,
          context: additionalContext,
          ...(inputExtension?.mapRequest?.({
            values: { userInputQuestion, selectedSources, ...restValues },
            resourceType,
          }) ?? {}),
          ...(toneOfVoiceId !== undefined && { toneOfVoiceId }),
        },
      });

      // Set the question input field to empty
      setValue("userInputQuestion", "");

      // Scroll to the bottom of the question list when question is asked
      scrollToBottomOfAiChat();
    });

  // Sends the prompt straight away with autoAnswerSuggestedPrompt. Otherwise it goes into the input
  // after anything already typed, ready to edit, and sending it is an ordinary question.
  const selectSuggestedQuestion: SelectSuggestedQuestion = async (prompt, trackingProperties) => {
    if (autoAnswerSuggestedPrompt) {
      setValue("userInputQuestion", prompt);
      await handleQuestionSubmit(true, trackingProperties)();
      return;
    }

    const currentText = getValues("userInputQuestion");
    const text = currentText.trim() ? `${currentText.trimEnd()}\n${prompt}` : prompt;
    // Validating clears an error left over from an earlier too-short question
    setValue("userInputQuestion", text, { shouldValidate: true });
    logEvent(EventNames.quickPromptClicked, {
      ...questionEventProperties(prompt),
      ...trackingProperties,
    });
  };

  const handleCopyToClipboard = async (answer: string) => {
    const copied = await copyToClipboard({ plain: answer });
    if (copied) {
      logEvent(EventNames.copyToClipboardClicked, {
        "Conversation Id": chatId,
        "Resource Id": resourceId,
        "Resource name": resourceName,
        "Context source": "Ask AI",
      });
      void toast.success("Answer copied");
    } else {
      void toast.error("Failed to copy");
    }
  };

  const onNewChatClick = () => {
    setChatId("");
    resetAiChatSubscription();
    onNewConversation?.();
    logEvent(EventNames.newChatCreated, {
      "Context source": "Ask AI",
      "Resource Id": resourceId,
      "Resource name": resourceName,
    });
  };

  const { title, subtitle, inputPlaceholder } = { ...defaultText, ...uiText };

  if (!currentUser) return <></>;

  return (
    <AiChatForm onSubmit={(e) => handleQuestionSubmit()(e)}>
      <Flex height="100%">
        {showHistory && historyPlacement === "sidebar" && (
          <FormProvider {...methods}>
            <AiChatHistory
              onNewChatClick={onNewChatClick}
              onSelectConversation={setChatId}
              currentConversationId={conversation.conversationId}
            />
          </FormProvider>
        )}
        <Flex column flexGrow={1} minWidth={0} width={1800}>
          {showHistory && historyPlacement === "inline" && !inlineHistoryVisible && (
            <StickyToolbar>
              <Text ellipsis ellipsisMethod="whiteSpace" bold>
                {conversation && conversation?.messages[0]?.question}
              </Text>
              <Flex justifyContent="flex-end" gap={4}>
                {conversation && (
                  <Button buttonType="secondary" icon="edit05" onClick={onNewChatClick}>
                    New chat
                  </Button>
                )}
                <Button
                  buttonType="secondary"
                  icon="clockRewind"
                  onClick={() => setInlineHistoryVisible(true)}
                >
                  History
                </Button>
              </Flex>
            </StickyToolbar>
          )}
          <MainContainer padding={mainContainerPadding}>
            {inlineHistoryVisible ? (
              <FormProvider {...methods}>
                <AiChatHistory
                  variant="inline"
                  onNewChatClick={onNewChatClick}
                  onBackClick={() => setInlineHistoryVisible(false)}
                  onSelectConversation={(conversationId: string) => {
                    setInlineHistoryVisible(false);
                    setChatId(conversationId);
                  }}
                  currentConversationId={conversation.conversationId}
                  containerPadding={inlineHistoryContainerPadding}
                />
              </FormProvider>
            ) : !chatId ? (
              <Flex column justifyContent="space-between" height="100%">
                {renderEmptyThread ? (
                  renderEmptyThread({
                    methods,
                    handleQuestionSubmit,
                    isLoading,
                    selectSuggestedQuestion,
                  })
                ) : (
                  <EmptyThreadPlaceholder>
                    {title && (
                      <TitleIconContainer>
                        <UIcon icon="stars2" size={24} color="white" />
                      </TitleIconContainer>
                    )}
                    <Text h2 center>
                      {title}
                    </Text>
                    <Text center>{subtitle}</Text>
                  </EmptyThreadPlaceholder>
                )}

                {/* Render suggested questions if there are no Q&A pairs */}
                <SuggestedQuestionsContainer layout={suggestedQuestionsLayout}>
                  {!isLoading &&
                    suggestedQuestions.length > 0 &&
                    suggestedQuestions.map((q) => (
                      <AiChatSuggestedQuestionContainer
                        {...q}
                        key={q.title}
                        onClick={() => selectSuggestedQuestion(q.prompt)}
                      />
                    ))}
                </SuggestedQuestionsContainer>
              </Flex>
            ) : (
              <ChatThreadContainer ref={containerRef} padding={chatThreadContainerPadding}>
                {/* Render current Q&A pairs for conversation */}
                {conversation.messages.map(({ question, answer, sources }, index) => {
                  const hasSources = Boolean(sources && sources.length > 0);
                  // Show skeleton loading for the last response
                  const isWaitingForLastResponseToStart =
                    index === conversation.messages.length - 1 && isWaitingForAnswerToStart;

                  // Hide the copy button for the last response
                  const isLastResponseLoading =
                    index === conversation.messages.length - 1 && isLoading;

                  return (
                    <div key={index}>
                      <AiQuestionAndAnswer
                        question={question}
                        answer={answer.replace(/\\n/g, "\n")}
                        isLoading={isWaitingForLastResponseToStart}
                        isStreaming={isLastResponseLoading}
                        saveQuestionComponent={renderSaveQuestion && renderSaveQuestion()}
                        thinking={thinking}
                        sources={sources as AiSource[]}
                        type={userInitialsType}
                        onDocumentSourceClick={onDocumentSourceClick}
                      />
                      {answer &&
                        !isLastResponseLoading &&
                        (renderActions ? (
                          renderActions(
                            answer,
                            conversation.conversationId,
                            (sources ?? []) as AiSource[],
                          )
                        ) : (
                          <ActionsContainer>
                            {/* Render sources if available */}
                            {hasSources && (
                              <SourcesContainer>
                                <AiSources sources={sources as AiSource[]} />
                              </SourcesContainer>
                            )}
                            {showCopyToClipboardButton && (
                              <CopyButtonContainer hasSources={hasSources}>
                                <Button
                                  icon="copy02"
                                  buttonType="secondary"
                                  onClick={() => handleCopyToClipboard(answer)}
                                >
                                  Copy to clipboard
                                </Button>
                              </CopyButtonContainer>
                            )}
                          </ActionsContainer>
                        ))}
                    </div>
                  );
                })}

                {/* Used to scroll to the bottom of the question list */}
                <span style={{ visibility: "hidden" }} />
              </ChatThreadContainer>
            )}
            {!inlineHistoryVisible && (
              <Flex column gap={16}>
                {renderInput ? (
                  renderInput({
                    methods,
                    handleQuestionSubmit,
                    isLoading,
                    currentConversation: conversation,
                    selectSuggestedQuestion,
                  })
                ) : (
                  <Card>
                    <Flex column alignItems="center" gap={16}>
                      <ClassNames>
                        {({ css }) => {
                          const placeholder =
                            inputPlaceholder && inputPlaceholder?.length > 100
                              ? inputPlaceholder.slice(0, 100) + "..."
                              : inputPlaceholder;
                          return (
                            <TextArea
                              autoSize={{ minRows: 3, maxRows: 3 }}
                              autoFocus
                              name="userInputQuestion"
                              label={""}
                              control={control}
                              className={css({
                                width: "100%",
                                borderRadius: 4,
                              })}
                              placeholder={placeholder}
                              disabled={isLoading}
                              aria-label="Ask a question"
                              showErrors={false}
                              onPressEnter={async (e) => {
                                if (e.shiftKey) {
                                  // Allow newline when Shift + Enter is pressed
                                  return;
                                }
                                // Prevent newline and trigger submission on Enter without Shift
                                e.preventDefault();
                                e.stopPropagation();
                                await handleQuestionSubmit()();
                              }}
                            />
                          );
                        }}
                      </ClassNames>
                      <Flex
                        justifyContent={
                          hasSourceSelector || inputExtension?.renderControls
                            ? "space-between"
                            : "flex-end"
                        }
                        width="100%"
                        alignItems="center"
                        gap={12}
                        flexWrap="wrap"
                      >
                        {inputExtension?.renderControls?.({
                          control,
                          methods,
                          values,
                          resourceType,
                        })}
                        {hasSourceSelector && (
                          <Popover
                            trigger={
                              <Button buttonType="secondary" icon="settings03">
                                Sources
                                <Badge hide={!selectedSources?.length}>
                                  {selectedSources?.length}
                                </Badge>
                              </Button>
                            }
                            align="start"
                            side="top"
                            hideArrow
                            contentPadding={0}
                          >
                            <CustomerDocumentSelector
                              title={<Text color="sysTextSecondary">{sourceSelector.title}</Text>}
                              inputPlaceholder={sourceSelector.inputPlaceholder}
                              documentType={sourceSelector.documentType}
                              control={control}
                              checkable
                              name="selectedSources"
                            />
                          </Popover>
                        )}
                        {isLoading ? (
                          <Spin />
                        ) : (
                          <Button
                            buttonType="primary"
                            disabled={
                              Boolean(formState.errors.userInputQuestion) || hasExtensionSubmitError
                            }
                            type="submit"
                            aria-label="Ask question"
                            icon="arrowUp"
                            iconProps={{ size: 16 }}
                            style={{ borderRadius: 4 }}
                          />
                        )}
                      </Flex>
                    </Flex>
                  </Card>
                )}
                {showDisclaimer && (
                  <Text description center>
                    AI-generated responses may be inaccurate. Stotles doesn't use your data to train
                    its models.
                  </Text>
                )}
              </Flex>
            )}
          </MainContainer>
        </Flex>
      </Flex>
    </AiChatForm>
  );
}

const AiChatForm = styled.form({
  height: "100%",
});

const MainContainer = styled.div<{ padding?: string }>(({ padding = "16px 48px 16px 32px" }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  justifyContent: "space-between",
  borderRadius: 8,
  width: "100%",
  flexGrow: 1,
  padding,
  minWidth: 0,
  minHeight: 0,
}));

const ChatThreadContainer = styled(Flex)<{ padding?: string }>(({ padding = "48px 16px 0 0" }) => ({
  flexDirection: "column",
  padding,
  marginBottom: 16,
  gap: 32,
  overflowY: "auto",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  flexGrow: 1,
  minHeight: 0,
}));

const TitleIconContainer = styled(Flex)(({ theme }) => ({
  height: 40,
  width: 40,
  backgroundColor: theme.colors.sysBrandBlueDefault,
  borderRadius: 4,
  alignItems: "center",
  justifyContent: "center",
}));

const SuggestedQuestionsContainer = styled.div<{
  layout?: "vertical" | "horizontal";
}>(({ layout = "horizontal" }) => ({
  display: "grid",
  marginBottom: 16,
  gridTemplateColumns: layout === "horizontal" ? "1fr 1fr" : "1fr",
  gap: 16,
}));

const EmptyThreadPlaceholder = styled(Flex)({
  marginTop: 48,
  flexDirection: "column",
  gap: 16,
  alignItems: "center",
});

const ActionsContainer = styled(Flex)({
  marginLeft: 40,
  alignItems: "flex-start",
  gap: 12,
  position: "relative",
  minHeight: 32,
});

const SourcesContainer = styled.div({
  marginTop: 4,
});

const CopyButtonContainer = styled.div<{ hasSources: boolean }>(({ hasSources }) => ({
  position: "absolute",
  top: 0,
  left: hasSources ? 120 : 0,
}));

const StickyToolbar = styled(Flex)({
  background: "linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, #FFF 30%)",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  position: "sticky",
  gap: 10,
  top: 0,
});
